import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueries } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import { Button } from "@/components/ui/button";
import { PencilLine, ArrowLeft, Download } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Services
import { getCash } from "@/services/finance";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/cash-flow-indirect-method/"
)({
  component: RouteComponent, // Use App instead of RouteComponent
});

function RouteComponent() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId");
  const year = Number(localStorage.getItem("year"));
  const theType = "INDIRECT";
  const { company_financial_report } = useAccessPermission();
  React.useEffect(() => {
    if (!year) {
      navigate({ to: "/report/finance/company/cash-flow-indirect-method/add" });
    }
  }, [year, navigate]);

  const queries = useQueries({
    queries: [
      {
        queryKey: ["getCashIndirect", companyId, year],
        queryFn: () => getCash(companyId as string, year, theType),
        enabled: !!companyId && !!year,
      },
      {
        queryKey: ["getCashIndirect", companyId, year - 1],
        queryFn: () => getCash(companyId as string, year - 1, theType),
        enabled: !!companyId && !!year,
      },
    ],
  });

  const [
    { data, isLoading, error },
    { data: lastdata, isLoading: lastisLoading, error: lasterror },
  ] = queries;

  React.useEffect(() => {
    if (!data?.data && !isLoading) {
      navigate({ to: "/report/finance/company/cash-flow-indirect-method/add" });
    }
  }, [data, navigate, isLoading]);
  const [unit, setUnit] = React.useState("1");

  const dataLoading = isLoading || lastisLoading;

  const cashData = data?.data;

  const getSectionTitle = (type: string) => {
    const titles: Record<string, string> = {
      OPERATING_A: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
      INVESTING_B: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການລົງທຶນ",
      FINANCING_C: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການປະກອບທຶນ",
      CASH_YEARLY: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
      // ANNUAL_NET: "ກຳໄລປະຈຳປີ",
    };
    return titles[type];
  };

  const thisYearDetails = [
    {
      type: "OPERATING_A",
      data: data?.data?.cashDetails?.filter(
        (detail) => detail.cashDetailType === "OPERATING_A"
      ),
    },
    {
      type: "INVESTING_B",
      data: data?.data?.cashDetails?.filter(
        (detail) => detail.cashDetailType === "INVESTING_B"
      ),
    },
    {
      type: "FINANCING_C",
      data: data?.data?.cashDetails?.filter(
        (detail) => detail.cashDetailType === "FINANCING_C"
      ),
    },
    {
      type: "CASH_YEARLY",
      data: data?.data?.cashDetails?.filter(
        (detail) => detail.cashDetailType === "CASH_YEARLY"
      ),
    },
  ];

  const lastYearDetails = [
    {
      type: "OPERATING_A",
      data: lastdata?.data?.cashDetails?.filter(
        (detail) => detail.cashDetailType === "OPERATING_A"
      ),
    },
    {
      type: "INVESTING_B",
      data: lastdata?.data?.cashDetails?.filter(
        (detail) => detail.cashDetailType === "INVESTING_B"
      ),
    },
    {
      type: "FINANCING_C",
      data: lastdata?.data?.cashDetails?.filter(
        (detail) => detail.cashDetailType === "FINANCING_C"
      ),
    },
    {
      type: "CASH_YEARLY",
      data: lastdata?.data?.cashDetails?.filter(
        (detail) => detail.cashDetailType === "CASH_YEARLY"
      ),
    },
  ];

  const calculateTotal = (
    cashDetails: {
      cashDetailType: string;
      amount: string;
      cashDataType?: string;
    }[],
    type: string
  ): number => {
    return cashDetails
      .filter((detail) => detail?.cashDetailType === type)
      .reduce((sum, detail) => {
        // Convert the amount to a processable number
        let numericAmount = 0;

        if (detail?.amount) {
          // Remove commas and parentheses
          const cleanAmount = String(detail?.amount).replace(/[,()]/g, "");
          numericAmount = Number(cleanAmount);

          // If original amount was in parentheses (expense) or cashDataType is EXPENSE, make it negative
          if (
            detail?.cashDataType === "EXPENSE" ||
            String(detail?.amount).startsWith("(")
          ) {
            numericAmount = -Math.abs(numericAmount);
          }
        }

        return sum + numericAmount;
      }, 0);
  };

  const totalOperatingA = React.useMemo(
    () =>
      calculateTotal(
        thisYearDetails.flatMap((item) => item.data),
        "OPERATING_A"
      ),
    [thisYearDetails]
  );

  const totalLastOperatingA = React.useMemo(
    () =>
      calculateTotal(
        lastYearDetails.flatMap((item) => item.data),
        "OPERATING_A"
      ),
    [lastYearDetails]
  );

  const totalInvestingB = React.useMemo(
    () =>
      calculateTotal(
        thisYearDetails.flatMap((item) => item.data),
        "INVESTING_B"
      ),
    [thisYearDetails]
  );

  const totalLastYearInvestingB = React.useMemo(
    () =>
      calculateTotal(
        lastYearDetails.flatMap((item) => item.data),
        "INVESTING_B"
      ),
    [lastYearDetails]
  );

  const totalFinancingC = React.useMemo(
    () =>
      calculateTotal(
        thisYearDetails.flatMap((item) => item.data),
        "FINANCING_C"
      ),
    [thisYearDetails]
  );

  const totalLastFinancingC = React.useMemo(
    () =>
      calculateTotal(
        lastYearDetails.flatMap((item) => item.data),
        "FINANCING_C"
      ),
    [lastYearDetails]
  );

  const { thisYearCashYearly, lastYearCashYearly } = React.useMemo(() => {
    const thisYearCashYearlyItems = thisYearDetails
      .flatMap((item) => item?.data)
      .filter((detail) => detail?.cashDetailType === "CASH_YEARLY");

    const lastYearCashYearlyItems = lastYearDetails
      .flatMap((item) => item?.data)
      .filter((detail) => detail?.cashDetailType === "CASH_YEARLY");

    const getNumericAmount = (detail: any) => {
      if (!detail?.amount) return 0;

      const cleanAmount = String(detail?.amount).replace(/[,()]/g, "");
      return Number(cleanAmount);
    };

    const thisYearFirstAmount = getNumericAmount(thisYearCashYearlyItems[0]);
    const thisYearLastAmount = getNumericAmount(
      thisYearCashYearlyItems[thisYearCashYearlyItems.length - 1]
    );

    const lastYearFirstAmount = getNumericAmount(lastYearCashYearlyItems[0]);
    const lastYearLastAmount = getNumericAmount(
      lastYearCashYearlyItems[lastYearCashYearlyItems.length - 1]
    );

    const thisYearCashYearly = thisYearLastAmount - thisYearFirstAmount;
    const lastYearCashYearly = lastYearLastAmount - lastYearFirstAmount;

    return { thisYearCashYearly, lastYearCashYearly };
  }, [thisYearDetails, lastYearDetails]);

  const formatNumberWithCommas = (
    value: number | string,
    type: "INCOME" | "EXPENSE"
  ) => {
    if (!value || value === "-") return "0.00";

    // Convert to string and remove commas
    const stringValue = String(value).replace(/,/g, "");

    // Get absolute value for display
    const numericValue = Math.abs(Number(stringValue.replace(/[()]/g, "")));

    // If the value is invalid (NaN), return formatted zero
    if (Number.isNaN(numericValue)) {
      return "0.00";
    }

    // Format with commas and 2 decimal places
    const formattedValue = numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Return with parentheses for EXPENSE type
    return type === "EXPENSE" ? `(${formattedValue})` : formattedValue;
  };

  const renderSummaryRow = (type: string) => {
    switch (type) {
      case "OPERATING_A":
        return (
          <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
            <TableCell>ກະແສເງິນສົດລວມ ທີ່ຕິດພັນກັບການທຸລະກິດ (ກ)</TableCell>
            <TableCell className="text-right">
              {(totalOperatingA / unit).toLocaleString()}
            </TableCell>
            <TableCell className="text-right">
              {(totalLastOperatingA / unit).toLocaleString()}
            </TableCell>
            <TableCell> </TableCell>
          </TableRow>
        );

      case "INVESTING_B":
        return (
          <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
            <TableCell>ກະແສເງິນສົດລວມສຸດທິ ຈາກການລົງທຶນ (ຂ)</TableCell>
            <TableCell className="text-right">
              {(totalInvestingB / unit).toLocaleString()}
            </TableCell>
            <TableCell className="text-right">
              {(totalLastYearInvestingB / unit).toLocaleString()}
            </TableCell>
            <TableCell> </TableCell>
          </TableRow>
        );

      case "FINANCING_C":
        return (
          <>
            <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
              <TableCell>ກະແສເງິນສົດລວມສຸດທິ ຈາກການປະກອບທຶນ (ຄ)</TableCell>
              <TableCell className="text-right">
                {(totalFinancingC / unit).toLocaleString()}
              </TableCell>
              <TableCell className="text-right">
                {(totalLastFinancingC / unit).toLocaleString()}
              </TableCell>
              <TableCell> </TableCell>
            </TableRow>

            <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
              <TableCell>
                ສ່ວນປ່ຽນແປງກະແສເງິນສົດລວມໃນປີ ( ກ + ຂ + ຄ ){" "}
              </TableCell>
              <TableCell className="text-right">
                {(
                  (totalOperatingA + totalInvestingB + totalFinancingC) /
                  unit
                ).toLocaleString()}
              </TableCell>
              <TableCell className="text-right">
                {(
                  (totalLastOperatingA +
                    totalLastYearInvestingB +
                    totalLastFinancingC) /
                  unit
                ).toLocaleString()}
              </TableCell>
              <TableCell> </TableCell>
            </TableRow>
          </>
        );

      default:
        break;
    }
  };

  const renderRows = (thisYearData, lastYearData) => {
    const mergedDetails = thisYearData?.map((thisYearDetail, index) => {
      // Find corresponding last year data based on 'name'
      const lastYearDetail = lastYearData?.find(
        (last) => last.name === thisYearDetail.name
      );

      console.log(lastYearDetail, "Last Year Detail");

      // If no matching last year data, set it as "-".
      const lastYearAmount = lastYearDetail ? lastYearDetail?.amount : "-";
      const thisYearAmount = thisYearDetail?.amount;

      return (
        <TableRow key={index}>
          <TableCell className="border">{thisYearDetail.name}</TableCell>
          <TableCell className="text-right border">
            {formatNumberWithCommas(
              thisYearAmount / unit,
              thisYearDetail?.cashDataType
            )}
          </TableCell>
          <TableCell className="text-right border">
            {formatNumberWithCommas(
              lastYearAmount / unit,
              lastYearDetail?.cashDataType
            )}
          </TableCell>
          <TableCell className="border">{thisYearDetail.remark}</TableCell>
        </TableRow>
      );
    });
    return mergedDetails;
  };

  if (dataLoading) {
    return <DinamixSkeletonTable />;
  }
  if (error) return <div>Error loading data</div>;

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() => navigate({ to: "/report/finance" })}
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>

          <CardTitle>
            ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {cashData?.yearly}
          </CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>

          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/cash-flow-indirect-method/edit",
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* <div className="flex flex-col gap-2 mb-4">
						<Label>ຫົວໜ່ວຍເງິນ</Label>
						<Select onValueChange={(value) => setUnit(Number(value))}>
							<SelectTrigger className="w-[150px]  ">
								<SelectValue placeholder="ເລືອກການສະແດງ" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="1">1</SelectItem>
								<SelectItem value="1000">1,000</SelectItem>
								<SelectItem value="1000000">1,000,000</SelectItem>
							</SelectContent>
						</Select>
					</div> */}

          <div className="mb-4">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <Table className="mb-4">
            <TableHeader>
              <TableRow className="[&>*]:font-bold	[&>*]:text-black [&>*]:whitespace-nowrap">
                <TableHead className="border">ເນຶ້ອໃນລາຍງານ</TableHead>
                <TableHead className="border">
                  ມູນຄ່າຍັງເຫຼືອ ({year})
                </TableHead>
                <TableHead className="border">
                  ມູນຄ່າຍັງເຫຼືອ ({year - 1})
                </TableHead>
                <TableHead className="border">ໝາຍເຫດ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Map over each section (OPERATING_A, INVESTING_B, etc.) */}
              {thisYearDetails.map((section, index) => (
                <React.Fragment key={section.type}>
                  <TableRow className="bg-[#216C4C]/20 font-bold">
                    <TableCell colSpan={4}>
                      {getSectionTitle(section.type)}
                    </TableCell>
                  </TableRow>
                  {renderRows(section.data, lastYearDetails[index].data)}
                  {renderSummaryRow(section.type)}
                </React.Fragment>
              ))}

              <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                <TableCell>ສ່ວນປ່ຽນແປງ ເງິນສົດລວມ</TableCell>
                <TableCell className="text-right">
                  {(thisYearCashYearly / unit).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(lastYearCashYearly / unit).toLocaleString()}
                </TableCell>
                <TableCell> </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {cashData?.docFile
            ? company_financial_report.isDownload && (
                <FileHandleViewer fileUrl={cashData?.docFile} />
              )
            : null}
        </CardContent>
      </Card>
    </section>
  );
}
